import type { Persona, ResearchPoll, ResearchPollVote } from '@repo/contracts';

import { handleOf } from './mentions';

/**
 * Everything a poll card says about its votes, worked out here — counts,
 * the average, who is torn, who went against the room — so none of it is
 * ever asked of a model or stored. ("Jev decides, code computes.")
 */

/** Top two options this close together means someone has not decided. */
const TORN_WITHIN = 0.15;

export interface PollSummary {
  /** One line for the top of the card. */
  headline: string;
  /** Voters whose top two options are too close to call. */
  torn: string[];
  /** Follow-ups worth asking next, as ready-to-send questions. */
  followUps: string[];
}

function first(name: string): string {
  return name.split(' ')[0] ?? name;
}

export function isTorn(vote: ResearchPollVote): boolean {
  const [top = 0, next = 0] = [...vote.weights].sort((a, b) => b - a);
  return top - next < TORN_WITHIN;
}

/** How many voted for each option, by their most likely pick. */
export function counts(poll: ResearchPoll): number[] {
  const tally = poll.options.map(() => 0);
  for (const vote of poll.votes) {
    if (vote.pick !== undefined) tally[vote.pick] = (tally[vote.pick] ?? 0) + 1;
  }
  return tally;
}

export function averageRating(poll: ResearchPoll): number | null {
  const values = poll.votes.flatMap((vote) =>
    vote.value === undefined ? [] : [vote.value],
  );
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** Items in overall order, most important first, by mean importance. */
export function overallOrder(poll: ResearchPoll): number[] {
  const means = poll.options.map(
    (_, i) =>
      poll.votes.reduce((sum, vote) => sum + (vote.weights[i] ?? 0), 0) /
      Math.max(poll.votes.length, 1),
  );
  return means
    .map((mean, i) => ({ i, mean }))
    .sort((a, b) => b.mean - a.mean)
    .map(({ i }) => i);
}

export function summarisePoll(
  poll: ResearchPoll,
  personas: Map<string, Persona>,
): PollSummary {
  const everyone = [...personas.values()];
  const nameOf = (id: string) => personas.get(id)?.name ?? 'Someone';
  const mention = (id: string) => {
    const persona = personas.get(id);
    return persona ? `@${handleOf(persona, everyone)}` : '';
  };
  const total = poll.votes.length;

  if (poll.format === 'rating') {
    const average = averageRating(poll) ?? 0;
    const values = poll.votes.map((v) => v.value ?? 0);
    const spread = Math.max(...values) - Math.min(...values);
    const lowest = [...poll.votes].sort(
      (a, b) => (a.value ?? 0) - (b.value ?? 0),
    )[0];
    const highest = [...poll.votes].sort(
      (a, b) => (b.value ?? 0) - (a.value ?? 0),
    )[0];
    const followUps: string[] = [];
    if (lowest) {
      followUps.push(
        `${mention(lowest.personaId)} what would move you up the scale?`,
      );
    }
    if (highest && spread >= 1.5 && highest.personaId !== lowest?.personaId) {
      followUps.push(
        `${mention(highest.personaId)} what makes you rate it higher than the others?`,
      );
    }
    return {
      followUps,
      headline: `Average ${average.toFixed(1)} of 5${
        total > 1
          ? spread < 1
            ? ' · they broadly agree'
            : ` · ${first(nameOf(lowest?.personaId ?? ''))} lowest, ${first(nameOf(highest?.personaId ?? ''))} highest`
          : ''
      }`,
      torn: [],
    };
  }

  if (poll.format === 'ranking') {
    const order = overallOrder(poll);
    const top = poll.options[order[0] ?? 0] ?? '';
    const dissenters = poll.votes.filter(
      (v) => (v.order ?? [])[0] !== order[0],
    );
    const followUps = dissenters.slice(0, 2).map((vote) => {
      const theirs = poll.options[(vote.order ?? [])[0] ?? 0] ?? '';
      return `${mention(vote.personaId)} why does "${theirs}" come first for you?`;
    });
    return {
      followUps,
      headline:
        dissenters.length === 0
          ? `Everyone puts "${top}" first`
          : `"${top}" comes first overall · ${String(total - dissenters.length)} of ${String(total)} agree`,
      torn: [],
    };
  }

  // choice and yes-no
  const tally = counts(poll);
  const leader = tally.indexOf(Math.max(...tally));
  const tied = tally.filter((n) => n === tally[leader]).length > 1;
  const torn = poll.votes.filter(isTorn).map((v) => v.personaId);
  const minority = poll.votes.filter(
    (v) => v.pick !== leader && !torn.includes(v.personaId),
  );

  const followUps: string[] = [];
  for (const id of torn.slice(0, 1)) {
    const vote = poll.votes.find((v) => v.personaId === id)!;
    const [a, b] = vote.weights
      .map((w, i) => ({ i, w }))
      .sort((x, y) => y.w - x.w)
      .map(({ i }) => poll.options[i] ?? '');
    followUps.push(
      `${mention(id)} what's making you torn between "${a ?? ''}" and "${b ?? ''}"?`,
    );
  }
  if (!tied) {
    for (const vote of minority.slice(0, 1)) {
      followUps.push(
        `${mention(vote.personaId)} why "${poll.options[vote.pick ?? 0] ?? ''}" rather than "${poll.options[leader] ?? ''}"?`,
      );
    }
  }

  const leaderLabel = poll.options[leader] ?? '';
  const headline = tied
    ? 'Split vote — no clear favourite'
    : tally[leader] === total
      ? `Everyone chose "${leaderLabel}"`
      : `${String(tally[leader])} of ${String(total)} chose "${leaderLabel}"`;

  return {
    followUps,
    headline:
      torn.length > 0
        ? `${headline} · ${torn.map((id) => first(nameOf(id))).join(', ')} torn`
        : headline,
    torn,
  };
}
